import { useEffect, useRef } from "react";
import { useLocation } from "react-router";

const fetchLatestBuildId = async (): Promise<string | null> => {
  try {
    const res = await fetch("/version.json", {
      cache: "no-store",
      headers: { "Cache-Control": "no-store" },
    });
    if (!res.ok) {
      return null;
    }

    const data = (await res.json()) as { buildId?: string; version?: string };
    return data.buildId || data.version || null;
  } catch {
    return null;
  }
};

export const BuildVersionGuard = () => {
  const { pathname, search } = useLocation();
  const checkingRef = useRef(false);

  useEffect(() => {
    if (import.meta.env.VITE_ENV === "development") {
      return;
    }

    let cancelled = false;
    const check = async () => {
      if (checkingRef.current) {
        return;
      }

      checkingRef.current = true;
      const latest = await fetchLatestBuildId();
      checkingRef.current = false;
      // biome-ignore lint/correctness/noUndeclaredVariables: __BUILD_ID__ is injected by Vite
      if (!cancelled && latest && latest !== __BUILD_ID__) {
        window.location.reload();
      }
    };
    check();
    return () => {
      cancelled = true;
    };
  }, [pathname, search]);

  useEffect(() => {
    if (import.meta.env.VITE_ENV === "development") {
      return;
    }

    const onFocus = () => {
      fetchLatestBuildId().then((latest) => {
        // biome-ignore lint/correctness/noUndeclaredVariables: __BUILD_ID__ is injected by Vite
        if (latest && latest !== __BUILD_ID__) {
          window.location.reload();
        }
      });
    };
    const onVisibility = () => {
      if (!document.hidden) {
        onFocus();
      }
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  return null;
};
